import { MINUTES_PER_DAY } from "./ruleDerivation";
import type { RuleConfig } from "./ruleTypes";

type DateParts = { year: number; month: number; day: number };

function readDateParts(ms: number, timeZone: string): DateParts {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(ms));
  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value);
  return { year: get("year"), month: get("month"), day: get("day") };
}

function timezoneOffsetMs(utcMs: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(utcMs));
  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value);
  const asUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour") === 24 ? 0 : get("hour"),
    get("minute"),
    get("second"),
  );
  return asUtc - utcMs;
}

function zonedTimeToUtcMs(
  { year, month, day }: DateParts,
  hour: number,
  minute: number,
  timeZone: string,
): number {
  let guessMs = Date.UTC(year, month - 1, day, hour, minute, 0);
  for (let i = 0; i < 2; i += 1) {
    const offset = timezoneOffsetMs(guessMs, timeZone);
    guessMs = Date.UTC(year, month - 1, day, hour, minute, 0) - offset;
  }
  return guessMs;
}

function addCalendarDays(parts: DateParts, days: number): DateParts {
  const shifted = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days));
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
  };
}

const WEEKDAY_TO_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

export function getDeviceTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/** Device-local weekday (0=Sun) and minute-of-day for schedule evaluation on the server. */
export function getDeviceLocalScheduleParts(
  nowMs: number,
  timeZone: string,
): { dayOfWeek: number; minuteOfDay: number } {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      weekday: "short",
      hourCycle: "h23",
      hour: "2-digit",
      minute: "2-digit",
    }).formatToParts(new Date(nowMs));
    const get = (type: string) => parts.find((part) => part.type === type)?.value ?? "";
    const weekday = get("weekday");
    const hour = Number(get("hour"));
    const minute = Number(get("minute"));
    return {
      dayOfWeek: WEEKDAY_TO_INDEX[weekday] ?? 0,
      minuteOfDay: hour * 60 + minute,
    };
  } catch {
    const date = new Date(nowMs);
    return { dayOfWeek: date.getUTCDay(), minuteOfDay: date.getUTCHours() * 60 + date.getUTCMinutes() };
  }
}

/**
 * Next scheduled activation instant (UTC ms) using the device's IANA timezone.
 * Mirrors `computeNextActivation` but safe to run in Convex (UTC runtime).
 */
export function computeNextActivationInTimeZone(
  rule: Pick<RuleConfig, "days" | "window">,
  nowMs: number,
  timeZone: string,
): number {
  const days = new Set(rule.days);
  const { dayOfWeek: nowDay, minuteOfDay: nowMinute } = getDeviceLocalScheduleParts(nowMs, timeZone);
  const { startMinute } = rule.window;
  const startHour = Math.floor(startMinute / 60);
  const startMinuteOfHour = startMinute % 60;

  let nowParts: DateParts;
  try {
    nowParts = readDateParts(nowMs, timeZone);
  } catch {
    const date = new Date(nowMs);
    return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + 7, startHour, startMinuteOfHour, 0, 0).getTime();
  }

  for (let offset = 0; offset <= 7; offset += 1) {
    const day = (nowDay + offset) % 7;
    if (!days.has(day)) continue;
    if (offset === 0 && nowMinute >= startMinute) continue;
    try {
      const targetParts = addCalendarDays(nowParts, offset);
      return zonedTimeToUtcMs(targetParts, startHour, startMinuteOfHour, timeZone);
    } catch {
      continue;
    }
  }

  const fallbackParts = addCalendarDays(nowParts, 7);
  try {
    return zonedTimeToUtcMs(fallbackParts, startHour, startMinuteOfHour, timeZone);
  } catch {
    return nowMs + 7 * MINUTES_PER_DAY * 60 * 1000;
  }
}
